import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { getDatabase, ref, get } from 'firebase/database';
import toast from 'react-hot-toast';
import { 
  FaSearch, FaClock, FaTags, FaMoneyBillWave, FaTruck, 
  FaArrowLeft, FaStore, FaHeart, FaRegHeart, FaChevronRight, FaInstagram,
  FaWhatsapp
} from 'react-icons/fa';
import Loading from './common/Loading';
import logo from '../assets/ma2.png';
import styles from '../styles/search.module.css';

// ✅ FUNCIÓN SEGURA (FUERA DEL COMPONENTE)
const getLikesValue = (likes) => {
  if (likes === null || likes === undefined) return 0;
  if (typeof likes === 'number') return likes;
  if (typeof likes === 'object') {
    return likes.acumulado || likes.diario || 0;
  }
  return 0;
};

const Search = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [mercado, setMercado] = useState(null);
  const [locales, setLocales] = useState([]);
  const [localesFiltrados, setLocalesFiltrados] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [sugerenciasPopulares, setSugerenciasPopulares] = useState([]);
  
  const [filtros, setFiltros] = useState({
    abierto: false,
    seccion: '',
    metodoPago: '',
    envios: false
  });
  
  const [paginaActual, setPaginaActual] = useState(1);
  const [resultadosPorPagina] = useState(10);
  const [resultadosOtrosMercados, setResultadosOtrosMercados] = useState([]);
  const [mostrarOtros, setMostrarOtros] = useState(false);
  const [localesAleatorios, setLocalesAleatorios] = useState([]);
  const [seccionesDisponibles, setSeccionesDisponibles] = useState([]);
  const [metodosPagoDisponibles] = useState(['Efectivo', 'Tarjeta', 'Transferencia', 'CoDi']);
  const [orden, setOrden] = useState('destacados');

  useEffect(() => {
    const cargarDatos = async () => {
      try {
        setLoading(true);
        setError(null);
        const db = getDatabase();
        
        const mercadoSnap = await get(ref(db, `mercados/${id}`));
        if (!mercadoSnap.exists()) {
          setError('Mercado no encontrado');
          setLoading(false);
          return;
        }
        const mercadoData = { id, ...mercadoSnap.val() };
        setMercado(mercadoData);
        
        const sugerencias = mercadoData.sugerencias || ['Carnes', 'Frutas', 'Tortillas', 'Flores', 'Panadería'];
        setSugerenciasPopulares(sugerencias);
        
        const localesSnap = await get(ref(db, `mercados/${id}/locales`));
        let localesData = [];
        if (localesSnap.exists()) {
          const data = localesSnap.val();
          
          for (const [key, local] of Object.entries(data)) {
            localesData.push({
              id: key,
              ...local,
              corazones: getLikesValue(local.likes)
            });
          }
        }

        const localesOrdenados = ordenarLocales(localesData, 'destacados');
        setLocales(localesOrdenados);
        setLocalesFiltrados(localesOrdenados);

        const secciones = [...new Set(localesData.map(l => l.seccion).filter(Boolean))];
        setSeccionesDisponibles(secciones);
        
        const aleatorios = [...localesData]
          .sort(() => Math.random() - 0.5)
          .slice(0, 6);
        setLocalesAleatorios(aleatorios);
        
      } catch (error) {
        console.error('Error cargando datos:', error);
        setError('Error al cargar los datos');
        toast.error('Error al cargar los datos');
      } finally {
        setLoading(false);
      }
    };
    
    if (id) cargarDatos();
  }, [id]);

  const isLocalAbierto = useCallback((local) => {
    if (!local.horarios) return false;
    const ahora = new Date();
    const dias = ['domingo', 'lunes', 'martes', 'miercoles', 'jueves', 'viernes', 'sabado'];
    const diaActual = dias[ahora.getDay()];
    const horaActual = ahora.getHours().toString().padStart(2, '0') + ':' + ahora.getMinutes().toString().padStart(2, '0');
    
    const horario = local.horarios[diaActual];
    if (!horario || !horario.abierto) return false;
    
    return horaActual >= horario.apertura && horaActual <= horario.cierre;
  }, []);

  const getProximoCierre = (local) => {
    if (!local.horarios) return '';
    const ahora = new Date();
    const dias = ['domingo', 'lunes', 'martes', 'miercoles', 'jueves', 'viernes', 'sabado'];
    const diaActual = dias[ahora.getDay()];
    const horario = local.horarios[diaActual];
    if (!horario || !horario.abierto) return '';
    return horario.cierre;
  };

  const ordenarLocales = (localesList, tipoOrden = 'destacados') => {
    const lista = [...localesList];
    
    switch (tipoOrden) {
      case 'destacados':
        return lista.sort((a, b) => {
          const topA = a.top || null;
          const topB = b.top || null;
          
          if (topA !== null && topB !== null) return topA - topB;
          if (topA !== null) return -1;
          if (topB !== null) return 1;
          return (a.nombre || '').localeCompare(b.nombre || '');
        });
        
      case 'relevancia':
        return lista.sort((a, b) => (b.corazones || 0) - (a.corazones || 0));
        
      case 'nombre':
        return lista.sort((a, b) => (a.nombre || '').localeCompare(b.nombre || ''));
        
      default:
        return lista;
    }
  };

  const realizarBusqueda = useCallback(async (termino, filtrosAplicados) => {
    if (!termino.trim() && !filtrosAplicados.seccion && !filtrosAplicados.metodoPago) {
      const ordenados = ordenarLocales(locales, orden);
      setLocalesFiltrados(ordenados);
      setResultadosOtrosMercados([]);
      setMostrarOtros(false);
      return;
    }

    const terminoLower = termino.toLowerCase().trim();
    
    let resultados = locales.filter(local => {
      const coincideBusqueda = !terminoLower || 
        local.nombre?.toLowerCase().includes(terminoLower) ||
        local.productos?.some(p => p.toLowerCase().includes(terminoLower)) ||
        local.seccion?.toLowerCase().includes(terminoLower);
      
      if (!coincideBusqueda) return false;
      
      if (filtrosAplicados.abierto && !isLocalAbierto(local)) return false;
      if (filtrosAplicados.seccion && local.seccion !== filtrosAplicados.seccion) return false;
      
      if (filtrosAplicados.metodoPago) {
        const metodoMap = {
          'Efectivo': 'efectivo',
          'Tarjeta': 'tarjeta',
          'Transferencia': 'transferencia',
          'CoDi': 'codi'
        };
        const key = metodoMap[filtrosAplicados.metodoPago];
        if (!local.metodosPago || !local.metodosPago[key]) return false;
      }
      
      if (filtrosAplicados.envios && !local.hacenEnvios) return false;
      
      return true;
    });
    
    resultados = ordenarLocales(resultados, orden);
    setLocalesFiltrados(resultados);
    
    if (resultados.length > 0) {
      setMostrarOtros(false);
      setResultadosOtrosMercados([]);
      return;
    }
    
    if (terminoLower || filtrosAplicados.seccion) {
      await buscarEnOtrosMercados(terminoLower, filtrosAplicados);
    }
  }, [locales, isLocalAbierto, orden]);

  const buscarEnOtrosMercados = async (termino, filtrosAplicados) => {
    try {
      const db = getDatabase();
      const mercadosSnap = await get(ref(db, 'mercados'));
      if (!mercadosSnap.exists()) return;
      
      const otrosResultados = [];
      const data = mercadosSnap.val();
      
      for (const [key, mercadoData] of Object.entries(data)) {
        if (key === id) continue;
        if (!mercadoData.locales) continue;
        
        for (const [localKey, local] of Object.entries(mercadoData.locales)) {
          const localData = { id: localKey, ...local };
          
          // ✅ CORREGIDO: usar getLikesValue
          localData.corazones = getLikesValue(localData.likes);
          
          const coincideBusqueda = !termino ||
            localData.nombre?.toLowerCase().includes(termino) ||
            localData.productos?.some(p => p.toLowerCase().includes(termino)) ||
            localData.seccion?.toLowerCase().includes(termino);
          
          if (!coincideBusqueda) continue;
          if (filtrosAplicados.abierto && !isLocalAbierto(localData)) continue;
          if (filtrosAplicados.seccion && localData.seccion !== filtrosAplicados.seccion) continue;
          
          if (filtrosAplicados.metodoPago) {
            const metodoMap = {
              'Efectivo': 'efectivo',
              'Tarjeta': 'tarjeta',
              'Transferencia': 'transferencia',
              'CoDi': 'codi'
            };
            const key = metodoMap[filtrosAplicados.metodoPago];
            if (!localData.metodosPago || !localData.metodosPago[key]) continue;
          }
          
          if (filtrosAplicados.envios && !localData.hacenEnvios) continue;
          
          otrosResultados.push({
            ...localData,
            mercadoNombre: mercadoData.nombre,
            mercadoId: key
          });
        }
      }
      
      setResultadosOtrosMercados(otrosResultados);
      setMostrarOtros(otrosResultados.length > 0);
      
    } catch (error) {
      console.error('Error buscando en otros mercados:', error);
    }
  };

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      if (searchTerm.trim()) {
        realizarBusqueda(searchTerm, filtros);
      } else {
        setLocalesFiltrados(locales);
        setResultadosOtrosMercados([]);
        setMostrarOtros(false);
      }
    }, 300);
    
    return () => clearTimeout(delayDebounce);
  }, [searchTerm, filtros, locales, realizarBusqueda]);

  const handleFiltroChange = (key, value) => {
    setFiltros(prev => ({ ...prev, [key]: value }));
    setPaginaActual(1);
  };

  const indexUltimo = paginaActual * resultadosPorPagina;
  const indexPrimero = indexUltimo - resultadosPorPagina;
  const resultadosPagina = localesFiltrados.slice(indexPrimero, indexUltimo);
  const totalPaginas = Math.ceil(localesFiltrados.length / resultadosPorPagina);

  const irALocal = (localId, mercadoId) => {
    navigate(`/mercado/${mercadoId || id}/local/${localId}`);
  };

  // ✅ CORREGIDO: usar getLikesValue
  const renderCorazones = (cantidad) => {
    const valor = getLikesValue(cantidad);
    return (
      <span className={styles.corazones}>
        <FaHeart className={styles.corazonLleno} />
        <span className={styles.corazonNumero}>{valor}</span>
      </span>
    );
  };

  if (loading) {
    return (
      <>
        <header className={styles.searchHeader}>
          <div className={styles.searchHeaderContainer}>
            <Link to="/" className={styles.searchLogo}>
              <img src={logo} alt="Mercado Abierto" className={styles.searchLogoImg} />
            </Link>
          </div>
        </header>
        <Loading message="Cargando mercado..." />
        <footer className={styles.searchFooter}>
          <div className={styles.searchFooterContent}>
            <p>© {new Date().getFullYear()} mercadoabierto.online</p>
          </div>
        </footer>
      </>
    );
  }

  if (error || !mercado) {
    return (
      <>
        <header className={styles.searchHeader}>
          <div className={styles.searchHeaderContainer}>
            <Link to="/" className={styles.searchLogo}>
              <img src={logo} alt="Mercado Abierto" className={styles.searchLogoImg} />
            </Link>
          </div>
        </header>
        <div className={styles.errorContainer}>
          <p className={styles.errorMessage}>{error || 'Mercado no encontrado'}</p>
          <button onClick={() => navigate('/')} className={styles.reloadButton}>
            ← Volver al inicio
          </button>
        </div>
        <footer className={styles.searchFooter}>
          <div className={styles.searchFooterContent}>
            <p>© {new Date().getFullYear()} mercadoabierto.online</p>
          </div>
        </footer>
      </>
    );
  }

  return (
    <div className={styles.searchContainer}>

      <header className={styles.searchHeader}>
        <div className={styles.searchHeaderContainer}>
          <Link to="/" className={styles.searchLogo}>
            <img src={logo} alt="Mercado Abierto" className={styles.searchLogoImg} />
          </Link>
        </div>
      </header>

      <main className={styles.searchMainContent}>

        <button onClick={() => navigate(`/mercado/${id}`)} className={styles.backButton}>
          <FaArrowLeft size={16} />
          Volver al mercado
        </button>

        <section className={styles.heroCard}>
          <div className={styles.heroTop}>
            <div className={styles.storeIcon}>
              <FaStore />
            </div>
            <div>
              <h1 className={styles.searchTitle}>{mercado.nombre}</h1>
              <p className={styles.searchSubtitle}>¿Qué estás buscando?</p>
            </div>
          </div>

          <div className={styles.searchBar}>
            <FaSearch className={styles.searchIcon} />
            <input
              type="text"
              placeholder="Busca por producto, sección o nombre del local..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={styles.searchInput}
            />
          </div>

          {!searchTerm && sugerenciasPopulares.length > 0 && (
            <>
              <span className={styles.popularTitle}>Sugerencias populares</span>
              <div className={styles.tagsContainer}>
                {sugerenciasPopulares.map((item, index) => (
                  <button
                    key={index}
                    className={styles.tagButton}
                    onClick={() => setSearchTerm(item)}
                  >
                    <FaSearch size={12} />
                    {item}
                  </button>
                ))}
              </div>
            </>
          )}
        </section>

        <section className={styles.filtersCard}>
          <h3>Filtros</h3>
          <div className={styles.filtersGrid}>
            <label className={styles.filterBox}>
              <FaClock />
              <span>Abierto ahora</span>
              <input
                type="checkbox"
                checked={filtros.abierto}
                onChange={(e) => handleFiltroChange("abierto", e.target.checked)}
              />
            </label>

            <div className={styles.filterSelect}>
              <FaTags />
              <select
                value={filtros.seccion}
                onChange={(e) => handleFiltroChange("seccion", e.target.value)}
              >
                <option value="">Todas las secciones</option>
                {seccionesDisponibles.map((s, i) => (
                  <option key={i} value={s}>{s}</option>
                ))}
              </select>
            </div>

            <div className={styles.filterSelect}>
              <FaMoneyBillWave />
              <select
                value={filtros.metodoPago}
                onChange={(e) => handleFiltroChange("metodoPago", e.target.value)}
              >
                <option value="">Todos los pagos</option>
                {metodosPagoDisponibles.map((m, i) => (
                  <option key={i} value={m}>{m}</option>
                ))}
              </select>
            </div>

            <label className={styles.filterBox}>
              <FaTruck />
              <span>Envíos</span>
              <input
                type="checkbox"
                checked={filtros.envios}
                onChange={(e) => handleFiltroChange("envios", e.target.checked)}
              />
            </label>
          </div>
        </section>

        <section className={styles.resultsSection}>
          <div className={styles.resultsHeader}>
            <h3>
              {localesFiltrados.length} {localesFiltrados.length === 1 ? 'local encontrado' : 'locales encontrados'}
            </h3>
            <div className={styles.resultadosOrden}>
              <span>Ordenar por:</span>
              <select 
                value={orden} 
                onChange={(e) => setOrden(e.target.value)}
                className={styles.ordenSelect}
              >
                <option value="destacados">⭐ Destacados</option>
                <option value="relevancia">Relevancia ▼</option>
                <option value="nombre">Nombre A-Z</option>
              </select>
            </div>
          </div>

          <div className={styles.localesList}>
            {resultadosPagina.map((local) => {
              const abierto = isLocalAbierto(local);
              const cierre = getProximoCierre(local);
              const pagos = [];
              if (local.metodosPago?.efectivo) pagos.push("Efectivo");
              if (local.metodosPago?.tarjeta) pagos.push("Tarjeta");
              if (local.metodosPago?.transferencia) pagos.push("Transferencia");
              if (local.metodosPago?.codi) pagos.push("CoDi");

              return (
                <div
                  key={local.id}
                  className={styles.marketCard}
                  onClick={() => irALocal(local.id, id)}
                >
                  <img
                    src={local.portada || "/img/default-local.jpg"}
                    alt={local.nombre}
                    className={styles.marketImage}
                  />

                  <div className={styles.marketContent}>
                    <div className={styles.marketTitle}>
                      {local.nombre}
                      {local.top && (
                        <span className={styles.topBadge}>⭐ Destacado</span>
                      )}
                    </div>
                    
                    <span className={styles.mercadoOrigen}> {local.mercadoNombre}</span>

                    {local.seccion && (
                      <span className={styles.marketSection}>{local.seccion}</span>
                    )}

                    <div className={styles.marketStatus}>
                      {abierto ? (
                        <span className={styles.open}>🟢 Abierto ahora</span>
                      ) : (
                        <span className={styles.closed}>🔴 Cerrado</span>
                      )}
                    </div>

                    <div className={styles.paymentTags}>
                      {pagos.map((p, i) => (
                        <span key={i} className={styles.payment}>{p}</span>
                      ))}
                    </div>

                    <div className={styles.hearts}>
                      {renderCorazones(local.corazones)}
                    </div>
                  </div>

                  <div className={styles.arrowCard}>
                    <FaChevronRight />
                  </div>
                </div>
              );
            })}
          </div>

          {totalPaginas > 1 && (
            <div className={styles.paginacion}>
              {Array.from({ length: totalPaginas }, (_, i) => i + 1).map((num) => (
                <button
                  key={num}
                  className={`${styles.paginaBtn} ${num === paginaActual ? styles.active : ''}`}
                  onClick={() => setPaginaActual(num)}
                >
                  {num}
                </button>
              ))}
            </div>
          )}
        </section>

        {mostrarOtros && resultadosOtrosMercados.length > 0 && (
          <div className={styles.otrosMercadosSection}>
            <div className={styles.otrosHeader}>
              <FaStore size={20} />
              <h3>Lo encontramos en otros mercados</h3>
            </div>
            <p className={styles.otrosSub}>
              {resultadosOtrosMercados.length} {resultadosOtrosMercados.length === 1 ? 'local encontrado' : 'locales encontrados'} en otros mercados
            </p>

            <div className={styles.localesList}>
              {resultadosOtrosMercados.slice(0, 10).map((local) => {
                const abierto = isLocalAbierto(local);
                const cierre = getProximoCierre(local);
                const pagos = [];
                if (local.metodosPago?.efectivo) pagos.push("Efectivo");
                if (local.metodosPago?.tarjeta) pagos.push("Tarjeta");
                if (local.metodosPago?.transferencia) pagos.push("Transferencia");
                if (local.metodosPago?.codi) pagos.push("CoDi");

                return (
                  <div
                    key={local.id}
                    className={`${styles.marketCard} ${styles.otroMercadoCard}`}
                    onClick={() => irALocal(local.id, local.mercadoId)}
                  >
                    <img
                      src={local.portada || "/img/default-local.jpg"}
                      alt={local.nombre}
                      className={styles.marketImage}
                    />

                    <div className={styles.marketContent}>
                      <div className={styles.marketTitle}>{local.nombre}</div>
                      <span className={styles.mercadoOrigen}>🏪 {local.mercadoNombre}</span>

                      {local.seccion && (
                        <span className={styles.marketSection}>{local.seccion}</span>
                      )}

                      <div className={styles.marketStatus}>
                        {abierto ? (
                          <span className={styles.open}>🟢 Abierto ahora</span>
                        ) : (
                          <span className={styles.closed}>🔴 Cerrado</span>
                        )}
                      </div>

                      <div className={styles.paymentTags}>
                        {pagos.map((p, i) => (
                          <span key={i} className={styles.payment}>{p}</span>
                        ))}
                      </div>

                      <div className={styles.hearts}>
                        {renderCorazones(local.corazones)}
                      </div>
                    </div>

                    <div className={styles.arrowCard}>
                      <FaChevronRight />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

      </main>

      <footer className={styles.searchFooter}>
        <div className={styles.searchFooterGrid}>
          <div>
            <img src={logo} alt="logo" className={styles.searchFooterLogo} />
            <p>Plataforma digital que conecta a los mercados municipales de México contigo.</p>
          </div>
          <div>
            <h4>Enlaces rápidos</h4>
            <p onClick={() => navigate('/')}>Inicio</p>
            <p onClick={() => navigate('/')}>Explorar mercados</p>
          </div>
          <div>
            <h4>Síguenos</h4>
            <div className={styles.socials}>
              <FaInstagram size={28} />
              <FaWhatsapp size={28} />
            </div>
          </div>
        </div>
        <div className={styles.mapFooterBottom}>
          © {new Date().getFullYear()} mercadoabierto.online - Todos los derechos reservados
        </div>
      </footer>

    </div>
  );
};

export default Search;